import { HttpErrorResponse } from '@angular/common/http';

type ErrorSource = Record<string, unknown>;

export function extractApiErrorMessages(error: unknown): string[] {
  const source = error instanceof HttpErrorResponse ? error.error : error;
  const messages: string[] = [];

  const pushMessages = (value: unknown, field?: string): void => {
    const normalized = flattenMessages(value);

    if (normalized.length === 0) {
      return;
    }

    const prefix = field ? `${formatFieldName(field)}: ` : '';
    normalized.forEach(message => messages.push(`${prefix}${message}`));
  };

  if (isRecord(source)) {
    if (isRecord(source.errors)) {
      Object.entries(source.errors).forEach(([field, value]) => pushMessages(value, field));
    } else if (Array.isArray(source.errors)) {
      pushMessages(source.errors);
    }

    ['message', 'detail', 'title', 'error', 'description'].forEach(key => {
      if (key in source) {
        pushMessages(source[key]);
      }
    });

    if (messages.length === 0) {
      Object.values(source).forEach(value => pushMessages(value));
    }
  } else {
    pushMessages(source);
  }

  if (messages.length === 0 && error instanceof HttpErrorResponse) {
    pushMessages(error.message || error.statusText);
  }

  return Array.from(new Set(messages.filter(Boolean)));
}

function flattenMessages(value: unknown): string[] {
  if (value == null) {
    return [];
  }

  if (typeof value === 'string') {
    const sanitized = sanitizeMessage(value);
    return sanitized ? [sanitized] : [];
  }

  if (Array.isArray(value)) {
    return value.flatMap(item => flattenMessages(item));
  }

  if (isRecord(value)) {
    if ('description' in value) {
      return flattenMessages(value.description);
    }

    if ('message' in value) {
      return flattenMessages(value.message);
    }
  }

  return [String(value)];
}

function sanitizeMessage(value: string): string {
  const lines = value
    .replace(/\r/g, '\n')
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)
    .filter(line => !line.startsWith('at ') && !line.startsWith('--- End of stack trace'));

  if (!lines.length) {
    return '';
  }

  const firstLine = lines[0].replace(/^[A-Za-z0-9_.`]+(?:Exception|Error)\s*:\s*/, '').trim();
  return firstLine;
}

function formatFieldName(field: string): string {
  return field
    .replace(/\./g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function isRecord(value: unknown): value is ErrorSource {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
